import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
    get as _get,
} from "lodash";
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Auth from "../../../../data/auth-service";
import GCPService from "../../../../data/gcp-service";
import InstanceGroupService from "../../../../data/instancegroup-service";

import Validator, {
    useValidate,
} from "../../../../components/validator";

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
    },
    button: {
        marginRight: theme.spacing(1),
    },
    addButton: {
        float: 'right',
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: '#FFF',
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
}));

const NETWORK_TYPE = [
    { name: 'Share Within Network', value: 'shareWithinNetwork' },
    { name: 'Shared With Me', value: 'sharedWithMe' }
];

const CreateInstancegroup = () => {
    const classes = useStyles();
    const [hasChanges, setHasChanges] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [project, setProject] = useState(null);
    const routeParams = useParams();
    const instancegroupForm = useValidate({});
    const location = useLocation();
    const navigate = useNavigate();
    const [valid, setValid] = useState(false);
    const [networkType, setNetworkType] = useState("shareWithinNetwork");
    const [instancesList, setInstancesList] = useState({ instances: [] });
    const gcpPerms = Auth.permissions.get("GCP");
    const [readonly] = useState(!gcpPerms.write);
    const [updateInstance, setUpdateInstance] = useState();
    const [regionList, setRegionList] = useState([]);
    const [networkFieldDisabled, setNetworkFieldDisabled] = useState(false);
    const [subnetworkFieldDisabled, setSubnetworkFieldDisabled] = useState(false);
    const [sharedsubnetworkFieldDisabled, setSharedSubnetworkFieldDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [instancegroupdata, setInstanceGroupData] = useState({
        kind: "",
        id: "",
        creationTimestamp: "",
        name: null,
        description: null,
        network: null,
        sharednetwork: null,
        sharedSubnetwork: null,
        fingerprint: null,
        zone: null,
        selfLink: null,
        size: instancesList.length,
        region: null,
        subnetwork: null,
        regionName: null,
        networkName: null,
        zoneName: null,
        sharedSubnetworkName: null,
        subnetworkName: null
    });
    const [networkList, setNetworkList] = useState([]);
    const [sharedvpcList, setSharedVpcList] = useState([]);
    const [instanceOptions, setInstanceOptions] = useState([]);
    const [filteredInstances, setFilteredInstances] = useState([]);
    const zonesList = instancegroupdata.region ? regionList.filter((region) => region.name == instancegroupdata.regionName).map((region) => region.zones).flat() : [];
    const subnetworkList = instancegroupdata.network && instancegroupdata.networkName != null ? networkList.filter((network) => network.name === instancegroupdata.networkName).map(network => network.subnetworks).flat() : [];
    const [selectedInstances, setSelectedInstances] = useState([]);
    const [addedInstances, setAddedInstances] = useState([]);
    const [removedInstances, setRemovedInstances] = useState([]);
    const [initialUpdateInstances, setInitialUpdateInstances] = useState({ instances: [] });
    const [nameError, setNameError] = useState("");
    const nameReg = new RegExp('^[a-z0-9-]+$');

    const fetchNetworks = async () => {
        try {
            const networks = await GCPService.getNetworksMap();
            let networkItems = [...networks].map(([networkName, { selfLink, subnetworks }]) => {
                let subnetworkItems = [...subnetworks].map(([subnetworkName, subnetworkSelfLink]) => ({ 'name': subnetworkName, 'selfLink': subnetworkSelfLink }));
                return { 'name': networkName, 'selfLink': selfLink, 'subnetworks': subnetworkItems };
            });
            setNetworkList(networkItems);
        } catch (error) {
            console.log(`Error while maping networks:${error.message}`);
        }
    };

    const listInstances = async (zone, group) => {
        try {
            const res = await InstanceGroupService.getInstancesbyInstanceGroup(zone, group);
            if (res.items) {
                let t = res.items.map((b) => ({ 'name': GCPService.selfLinktoName(b.instance), 'selfLink': b.instance }));
                setInstancesList({ instances: t });
                setInitialUpdateInstances({ instances: t });
                setSelectedInstances(t);
            }
        } catch (error) {
            console.log(`Error while fetching Instances list: ${error.message}`);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [regions] = await Promise.all([
                    GCPService.getRegionsMap(),
                ]);
                let regionItems = [...regions].map(([regionName, { selfLink, zones }]) => {
                    let zoneItems = [...zones].map(([zoneName, zoneSelfLink]) => ({ 'name': zoneName, 'selfLink': zoneSelfLink }));
                    return { 'name': regionName, 'selfLink': selfLink, 'zones': zoneItems };
                });

                setRegionList(regionItems);
            } catch (error) {
                console.error(`Error fetching data:${error.message}`);
            }
        };
        fetchData();
    }, [])


    useEffect(() => {
        const getHost = async () => {
            try {
                const data = await GCPService.getXPNhosts();
                if (data.name) {
                    setProject(data.name)
                }
                return null;
            } catch (error) {
                console.log(`Error while fetching Hosts:${error.message}`);
            }
        };
        getHost();
        if (routeParams.id) {
            setIsUpdate(true);
            if (location?.state?.data) {
                let a = _cloneDeep(location.state.data);
                if (a.network) {
                    if (project && a.network.includes(project)) {
                        setNetworkType("sharedWithMe");
                        a.sharednetwork = a.network;
                        a.network = null;
                    } else {
                        setNetworkFieldDisabled(true);
                        a.networkName = GCPService.selfLinktoName(a.network);
                    }
                }

                if (a.subnetwork) {
                    if (project && a.subnetwork.includes(project)) {
                        setNetworkType("sharedWithMe");
                        setSharedSubnetworkFieldDisabled(true);
                        a.regionName = a.subnetwork.split("/regions/")[1].split("/")[0];
                        a.sharedSubnetwork = a.subnetwork;
                        a.sharedSubnetworkName = GCPService.selfLinktoName(a.subnetwork);
                        a.subnetwork = null;
                    } else {
                        setSubnetworkFieldDisabled(true);
                        a.subnetworkName = GCPService.selfLinktoName(a.subnetwork);
                    }
                }
                if (a.zone) {
                    a.zoneName = GCPService.selfLinktoName(a.zone);
                    listInstances(a.zoneName, a.name);
                    getInstances(a.zoneName);
                }
                setInstanceGroupData(a);
                setUpdateInstance(a);
            }
        }

    }, [routeParams.id]);

    useEffect(() => {
        const isValid =
            (instancegroupdata.name || "").length > 0 && (nameError === "") &&
            (isUpdate ? true : (instancegroupdata.region || "").length > 0) &&
            (instancegroupdata.zone || "").length > 0 &&
            (networkType === "shareWithinNetwork" ? (instancegroupdata.network || "").length > 0 : (instancegroupdata.sharednetwork || "").length > 0)
        setValid(isValid);
        setHasChanges(!_isEqual(instancegroupdata, updateInstance));
    }, [instancegroupdata, updateInstance, networkType]);

    useEffect(() => {
        setInstanceGroupData(prevData => ({
            ...prevData,
            size: instancesList.length
        }))
    }, [instancesList]);

    useEffect(() => {
        fetchNetworks();
        getSharedVPC();
    }, [networkType, instancegroupdata.regionName]);

    const handleAutocompletechange = (event, newValue, field) => {
        if (newValue) {
            var fieldName = field + 'Name';
            if (field === 'region') {
                setInstanceGroupData((prevData) => ({
                    ...prevData,
                    zoneName: null,
                    zone: null,
                    sharedSubnetwork: null,
                }));
                setSelectedInstances([]);
                setSharedVpcList([]);
            }
            instancegroupForm.set({ ...instancegroupForm.get(), [field]: newValue.selfLink });
            setInstanceGroupData((prevData) => ({
                ...prevData,
                [fieldName]: newValue.name,
                [field]: newValue.selfLink,
            }));
            if (field === 'zone') {
                getInstances(newValue.name);
            }
        }
    };

    const filterInstancesBySharedSubnetwork = (instances, sharedsubnetworkurl) => {
        return instances.filter((instance) => {
            return instance.networkInterfaces.some((network) => {
                if (network.name == 'nic0') {
                    return sharedsubnetworkurl === network.subnetwork;
                }
            })
        });
    };

    const filterInstancesBySubnetwork = (instances, subnetwork) => {
        return instances.filter((instance) => {
            return instance.networkInterfaces.some((network) => {
                if (network.name == 'nic0') {
                    let urlParts = network.subnetwork.split('/');
                    let subnetworkName = urlParts[urlParts.length - 1];
                    return subnetworkName === subnetwork;
                }
            })
        });
    }

    useEffect(() => {
        let instances = instanceOptions;
        if (networkType === "shareWithinNetwork") {
            if (instancegroupdata.subnetworkName) {
                instances = filterInstancesBySubnetwork(instances, instancegroupdata.subnetworkName);
            }
            setFilteredInstances(instances);
        }
        else {
            if (instancegroupdata.sharedSubnetwork) {
                instances = filterInstancesBySharedSubnetwork(instances, instancegroupdata.sharedSubnetwork);
                setFilteredInstances(instances);
            }
        }
    }, [instanceOptions, instancegroupdata.zoneName, instancegroupdata.subnetworkName, networkType, instancegroupdata.sharedSubnetworkName]);


    const getInstances = async (zone) => {
        try {
            await GCPService.getInstances(zone).then(res => {
                if (res.items) {
                    setInstanceOptions(res.items);
                } else {
                    setInstanceOptions([]);
                }
            })
        } catch (error) {
            console.log(`Error while fetching Instances:${error.message}`);
        }
    };

    const getSharedVPC = async () => {
        try {
            await GCPService.getXPNhosts().then(res => {
                if (res.name && instancegroupdata && instancegroupdata.regionName) {
                    GCPService.getSharedVPC(res.name, instancegroupdata.regionName).then(res => {
                        setSharedVpcList(res.items);
                    })
                }
            })
        } catch (error) {
            console.log(`Error while fetching Hosts:${error.message}`);
        }
    };

    const handleNetworkAutocomplete = (event, newValue, field) => {
        if (newValue) {
            var fieldName = field + 'Name';
            instancegroupForm.set({ ...instancegroupForm.get(), [field]: newValue.selfLink });
            setInstanceGroupData(prevData => ({
                ...prevData,
                subnetwork: null,
                [fieldName]: newValue.name,
                [field]: newValue.selfLink,
                subnetworkName: null,
                subnetwork: null
            }));
        }
    };

    const handlesharedVPCAutocomplete = (event, newValue, field) => {
        if (newValue) {
            instancegroupForm.set({ ...instancegroupForm.get(), [field]: newValue.selfLink });
            setInstanceGroupData(prevData => ({
                ...prevData,
                sharedSubnetworkName: newValue.name,
                sharednetwork: newValue.network,
                [field]: newValue.selfLink
            }));
        }
    };

    const handleClose = () => {
        navigate("/system/port_mirroring/gcp", { state: { tab: 0 } });
    };

    const handleInstanceGroupName = (e, validID, valid) => {
        const newName = e.target.value;

        if (!nameReg.test(newName)) {
            setNameError("Enter lowercases, numbers, and hypens");
        } else {
            setNameError("");
        }

        instancegroupForm.set({ ...instancegroupForm.get(), [validID]: valid });
        setInstanceGroupData(prevData => ({
            ...prevData,
            name: newName,
        }));
    }

    const handleInstanceGroupChange = (e, validID, valid) => {
        instancegroupForm.set({ ...instancegroupForm.get(), [validID]: valid });
        setInstanceGroupData(prevData => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };

    const handleNetworkChange = (e) => {
        setNetworkType(e.target.value);
        setSelectedInstances([]);
    };

    const handleInstanceChange = (event, newValue, field) => {
        const items = newValue.map((option) => ({ 'instance': option.selfLink }));
        const added = newValue.filter(instance => !initialUpdateInstances.instances.some(i => i.selfLink === instance.selfLink)).map((a) => ({ 'instance': a.selfLink }));
        const removed = initialUpdateInstances.instances.filter(instance => !newValue.some(i => i.selfLink === instance.selfLink)).map((a) => ({ 'instance': a.selfLink }));
        setInstancesList({ instances: items });
        setAddedInstances({ instances: added });
        setRemovedInstances({ instances: removed });
        setSelectedInstances(newValue);
    };

    const handleSubmit = async () => {
        setLoading(true);

        if (isUpdate) {
            try {

                if (addedInstances.instances && addedInstances.instances.length > 0) {
                    let w = await InstanceGroupService.addInstances(instancegroupdata.zoneName, instancegroupdata.name, addedInstances);
                }
                if (removedInstances.instances && removedInstances.instances.length > 0) {
                    let a = await InstanceGroupService.removeInstances(instancegroupdata.zoneName, instancegroupdata.name, removedInstances);
                }
                toast.success("Updated Instance group Successfully");
            }
            catch (error) {
                toast.error(`Error while Updating Instance Group : ${error.message}`);
            }

        } else {
            try {
                const instanceGroupBody = {
                    "name": instancegroupdata.name,
                    "description": instancegroupdata.description
                };
                let y = await InstanceGroupService.createInstanceGroup(instancegroupdata.zoneName, instanceGroupBody);
                if (y) {
                    if (addedInstances.instances && addedInstances.instances.length > 0) {
                        let w = await InstanceGroupService.addInstances(instancegroupdata.zoneName, instancegroupdata.name, addedInstances);
                    }
                }
                toast.success("Instance group created Successfully");
            } catch (error) {
                toast.error(`Error while Creating Instance Group: ${error.message}`);
            }
        }
        setLoading(false);
        navigate("/system/port_mirroring/gcp", { state: { tab: 0 } });
    };

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper>
                    <Box p={5}>
                        <Grid container>
                            <Grid item xs={12} container display="flex" direction='column' >
                                <Box>
                                    <Typography variant="h6">
                                        {readonly
                                            ? "View Instance Group"
                                            : (isUpdate
                                                ? 'Update Instance Group'
                                                : 'Create Instance Group')}
                                    </Typography>
                                </Box>
                                <Box mt={1}>
                                    <Typography variant="subtitle2">Create Instance Group
                                        Collection of virtual machine (VM) instances</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} container direction="row" spacing={2} marginTop={2}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <TextField
                                            disabled={readonly || isUpdate}
                                            label="Name"
                                            name="name"
                                            value={instancegroupdata.name || ""}
                                            onChange={(e, validID, valid) => {
                                                handleInstanceGroupName(e, validID, valid);
                                            }}
                                            error={nameError !== ""}
                                            helperText={`${nameError}`}
                                        ></TextField>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <Validator
                                        disabled={readonly || isUpdate}
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        value={instancegroupdata.description || ""}
                                        onChange={(e, validID, valid) => {
                                            handleInstanceGroupChange(e, validID, valid);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} container>
                                    <Typography variant="h7">Location</Typography>
                                    <Grid item xs={12} container spacing={2}>
                                        {!isUpdate ? <Grid item xs={3}>
                                            <FormControl fullWidth margin="normal">
                                                <Autocomplete
                                                    key={instancegroupdata.regionName}
                                                    disabled={readonly || isUpdate}
                                                    value={instancegroupdata.regionName || null}
                                                    onChange={(event, newValue) => {
                                                        handleAutocompletechange(
                                                            event,
                                                            newValue
                                                                ? typeof newValue === "object"
                                                                    ? newValue : null
                                                                : null,
                                                            "region")
                                                    }}
                                                    options={regionList}
                                                    isOptionEqualToValue={(option, value) => option.name === value}
                                                    getOptionLabel={(option) => typeof option === "object" ? `${option.name}` : option}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Region" />
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid> : null}
                                        <Grid item xs={3}>
                                            <FormControl fullWidth margin="normal">
                                                <Autocomplete
                                                    disabled={instancegroupdata.region === null || readonly || isUpdate}
                                                    value={instancegroupdata.zoneName}
                                                    onChange={(event, newValue) =>
                                                        handleAutocompletechange(
                                                            event,
                                                            newValue
                                                                ? typeof newValue === "object"
                                                                    ? newValue : null
                                                                : null,
                                                            "zone")
                                                    }
                                                    options={zonesList}
                                                    isOptionEqualToValue={(option, value) => option.name === value}
                                                    getOptionLabel={(option) => typeof option === "object" ? `${option.name}` : option}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Zone" />
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                </Grid>

                                <Grid item xs={12} container direction="row" spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl>
                                            <Typography variant="h7">Network & Instances</Typography>
                                            <Box mt={2}>
                                                <RadioGroup row onChange={handleNetworkChange} value={networkType}>
                                                    {NETWORK_TYPE.map((v, i) => (
                                                        <FormControlLabel
                                                            key={'home-page-' + i}
                                                            label={v.name}
                                                            aria-label={v.name}
                                                            value={v.value}
                                                            name="home"
                                                            disabled={isUpdate}
                                                            control={<Radio />}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </Box>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} container direction="row" spacing={2} mb={2}>
                                        {networkType === 'shareWithinNetwork' && (
                                            <>
                                                <Grid item xs={6}>
                                                    <FormControl fullWidth margin="normal">
                                                        <Autocomplete
                                                            disabled={readonly || (isUpdate && networkFieldDisabled)}
                                                            value={instancegroupdata.networkName}
                                                            onChange={(event, newValue) =>
                                                                handleNetworkAutocomplete(event, newValue, "network")
                                                            }
                                                            options={networkList}
                                                            getOptionLabel={(option) => typeof option === 'object' ? `${option.name}` : option}
                                                            renderInput={(params) => <TextField {...params} label="Network" />}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <FormControl fullWidth margin="normal">
                                                        <Autocomplete
                                                            disabled={instancegroupdata.network === null || readonly || (isUpdate && subnetworkFieldDisabled)}
                                                            value={instancegroupdata.subnetworkName}
                                                            onChange={(event, newValue) =>
                                                                handleAutocompletechange(
                                                                    event,
                                                                    newValue
                                                                        ? typeof newValue === "object"
                                                                            ? newValue : null
                                                                        : null,
                                                                    "subnetwork"
                                                                )
                                                            }
                                                            options={subnetworkList}
                                                            isOptionEqualToValue={(option, value) => option.name === value}
                                                            getOptionLabel={(option) => typeof option === 'object' ? `${option.name}` : option}
                                                            renderInput={(params) => <TextField {...params} label="Subnetwork" />}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </>
                                        )}
                                        {networkType === 'sharedWithMe' && (
                                            <Grid item xs={6}>
                                                <FormControl fullWidth margin="normal">
                                                    <Autocomplete
                                                        disabled={instancegroupdata.region === null || readonly || (isUpdate && sharedsubnetworkFieldDisabled)}
                                                        value={instancegroupdata.sharedSubnetworkName}
                                                        onChange={(event, newValue) =>
                                                            handlesharedVPCAutocomplete(event, newValue, "sharedSubnetwork")
                                                        }
                                                        options={sharedvpcList}
                                                        isOptionEqualToValue={(option, value) => option.name === value}
                                                        getOptionLabel={(option) => typeof option === 'object' ? `${option.name}` : option}
                                                        renderInput={(params) => <TextField {...params} label="Shared subnetwork" />}
                                                    />
                                                </FormControl>
                                            </Grid>
                                        )}

                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} container direction="row" spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="h7">VM Instance</Typography>
                                <Box mt={2}>
                                    <Autocomplete
                                        multiple
                                        id="checkboxes-tags-demo"
                                        value={selectedInstances}
                                        options={filteredInstances}
                                        disableCloseOnSelect
                                        disabled={(networkType === "shareWithinNetwork" ? instancegroupdata.subnetwork === null : instancegroupdata.sharedSubnetwork === null) || readonly}
                                        onChange={(event, newValue) =>
                                            handleInstanceChange(event, newValue, 'instance')
                                        }
                                        isOptionEqualToValue={(option, value) => option.name === value.name}
                                        getOptionLabel={(option) => option.name}
                                        renderOption={(props, option, { selected }) => {
                                            const { key, ...optionProps } = props;
                                            return (
                                                <li key={key} {...optionProps}>
                                                    <Checkbox
                                                        icon={icon}
                                                        checkedIcon={checkedIcon}
                                                        style={{ marginRight: 8 }}
                                                        checked={selected}
                                                    />
                                                    {option.name}
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="instances" placeholder="Instances" />
                                        )}
                                        ChipProps={{
                                            className: classes.chip,
                                        }}
                                    />

                                </Box>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} mt={2} mb={6} container justifyContent="flex-end">
                            <Box mr={2}>
                                <Button
                                    onClick={handleClose}
                                    variant="contained"
                                    color="secondary"
                                    className={classes.button}
                                    disabled={loading}
                                >
                                    {readonly ? "Back" : "Cancel"}
                                </Button>
                                {!readonly && (
                                    <Button
                                        onClick={handleSubmit}
                                        variant="contained"
                                        color="primary"
                                        className={classes.button}
                                        disabled={!valid || loading}
                                    >
                                        {isUpdate ? "Update" : "Save"}
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Box>
                </Paper >
            </Grid >
        </Grid >
    );
};

export default CreateInstancegroup;
